/**
 * UA 协议兼容配置（参考 meta-landing-page src/utils/uaConfig.ts）
 * 双协议运行时解析：weelife（默认优先）+ WBBridge（回退），
 * 单一构建产物可同时运行在两种 App 外壳中，不再依赖 .env 按构建目标硬编码单协议。
 */

/** Weelife 协议（默认优先） */
export const WEELIFE_PROTOCOL = {
  tag: 'weelife',
  language: 'weelife-language',
  data: 'weelife-data',
  unity: 'weelife-unity',
  commonHeaders: 'weelife-common-headers',
  bridgeTag: 'WeelifeJavascriptBridge',
  bridgeAndroidTag: 'WeelifeAppInterface'
}

/** WBBridge 协议（回退） */
export const WB_BRIDGE_PROTOCOL = {
  tag: 'WBBridge',
  language: 'WBBridge-language',
  data: 'WBBridge-data',
  unity: 'WBBridge-unity',
  commonHeaders: 'WBBridge-common-headers',
  bridgeTag: 'WBBridgeJSConnector',
  bridgeAndroidTag: 'WeelifeAppInterface'
}

/** 协议优先级：先 weelife，再 WBBridge */
export const UA_PROTOCOLS = [WEELIFE_PROTOCOL, WB_BRIDGE_PROTOCOL]

/** 兼容旧引用，默认指向 weelife 协议 */
export const UA_TAG = WEELIFE_PROTOCOL.tag
export const UA_LANGUAGE = WEELIFE_PROTOCOL.language
export const UA_DATA = WEELIFE_PROTOCOL.data
export const UA_UNITY = WEELIFE_PROTOCOL.unity
export const UA_COMMON_HEADERS = WEELIFE_PROTOCOL.commonHeaders
export const BRIDGE_TAG = WEELIFE_PROTOCOL.bridgeTag
export const BRIDGE_ANDROID_TAG = WEELIFE_PROTOCOL.bridgeAndroidTag

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const normalizeUa = (ua) => ua.toLowerCase()

export const uaIncludes = (ua, tag) => normalizeUa(ua).includes(tag.toLowerCase())

const protocolMatchesUa = (ua, protocol) =>
  uaIncludes(ua, protocol.tag) ||
  uaIncludes(ua, protocol.unity) ||
  uaIncludes(ua, protocol.language) ||
  uaIncludes(ua, protocol.data) ||
  uaIncludes(ua, protocol.commonHeaders) ||
  uaIncludes(ua, `${protocol.tag}-android`)

/** 按优先级解析 UA 命中的协议，浏览器环境返回 null */
export const resolveUaProtocol = (ua = navigator.userAgent) => {
  for (const protocol of UA_PROTOCOLS) {
    if (protocolMatchesUa(ua, protocol)) {
      return protocol
    }
  }
  return null
}

/** 桥接协议：未命中 UA 时默认 weelife */
export const resolveBridgeProtocol = (ua = navigator.userAgent) => {
  return resolveUaProtocol(ua) ?? WEELIFE_PROTOCOL
}

/** 是否任一协议的 App 内 UA（按 tag 判定） */
export const isInAppUA = (ua = navigator.userAgent) => UA_PROTOCOLS.some((protocol) => uaIncludes(ua, protocol.tag))

/** 是否 iOS App 外壳（按 language key 判定） */
export const isIOSAppUA = (ua = navigator.userAgent) => UA_PROTOCOLS.some((protocol) => uaIncludes(ua, protocol.language))

/** 是否 Unity 内嵌 UA（weelife-unity / WBBridge-unity） */
export const isUnityUA = (ua = navigator.userAgent) => UA_PROTOCOLS.some((protocol) => uaIncludes(ua, protocol.unity))

/** 是否安卓 App 外壳（weelife-android / WBBridge-android） */
export const isAndroidUA = (ua = navigator.userAgent) => UA_PROTOCOLS.some((protocol) => uaIncludes(ua, `${protocol.tag}-android`))

/** UA 中是否携带任一协议的 data 段 */
export const hasUaData = (ua = navigator.userAgent) => UA_PROTOCOLS.some((protocol) => uaIncludes(ua, protocol.data))

/** 解析 UA 携带的 language key（weelife-language / WBBridge-language），未命中返回 null */
export const resolveUaLanguageKey = (ua = navigator.userAgent) => {
  for (const protocol of UA_PROTOCOLS) {
    if (uaIncludes(ua, protocol.language)) {
      return protocol.language
    }
  }
  return null
}

/** 解析 UA 携带的 data key，未命中回退 weelife-data */
export const resolveUaDataKey = (ua = navigator.userAgent) => {
  for (const protocol of UA_PROTOCOLS) {
    if (uaIncludes(ua, protocol.data)) {
      return protocol.data
    }
  }
  return WEELIFE_PROTOCOL.data
}

/** 解析 UA 携带的 common-headers key，未命中回退 weelife-common-headers */
export const resolveUaCommonHeadersKey = (ua = navigator.userAgent) => {
  for (const protocol of UA_PROTOCOLS) {
    if (uaIncludes(ua, protocol.commonHeaders)) {
      return protocol.commonHeaders
    }
  }
  return WEELIFE_PROTOCOL.commonHeaders
}

/** 按协议解析后的 data key 构造 `key/({json})` 提取正则 */
export const getUaDataRegex = (ua = navigator.userAgent) => {
  const dataKey = resolveUaDataKey(ua)
  return new RegExp(`${escapeRegExp(dataKey)}\\/({[^}]+})`)
}

/** 取已挂载的 JS 桥对象（WeelifeJavascriptBridge / WBBridgeJSConnector），按协议优先级 */
export const getBridgeWindow = () => {
  for (const protocol of UA_PROTOCOLS) {
    const bridge = window[protocol.bridgeTag]
    if (bridge) {
      return bridge
    }
  }
  return undefined
}

/** 按当前 UA 命中协议挂载 JS 桥对象 */
export const setBridgeWindow = (bridge, ua = navigator.userAgent) => {
  window[resolveBridgeProtocol(ua).bridgeTag] = bridge
}

/** 取安卓原生桥（WeelifeAppInterface，双协议共用），需带 jsCallNative 方法 */
export const getAndroidBridgeInterface = () => {
  for (const protocol of UA_PROTOCOLS) {
    const bridge = window[protocol.bridgeAndroidTag]
    if (bridge?.jsCallNative) {
      return bridge
    }
  }
  return undefined
}

/** 按协议生成 App scheme（weelife://xxx 或 WBBridge://xxx） */
export const getAppSchemeUrl = (path, ua = navigator.userAgent) => {
  const protocol = resolveUaProtocol(ua) ?? WEELIFE_PROTOCOL
  return `${protocol.tag}://${path}`
}
